import { RefObject, useEffect, useRef } from "react";
import { CellSelection } from "../models/plan-types";
import { BLOCK_CLASS, columnAt, SELECTING_CLASS, SELECTION_CLASS, TRACK_CLASS } from "../components/plan-geometry";
import { PlanGrid, snapEnd, snapStart } from "../models/plan-scale";

/** Кнопки мыши в pointer-событиях */
const LEFT_BUTTON = 0;
const MIDDLE_BUTTON = 1;

export interface RangeSelectionOptions {
    containerRef: RefObject<HTMLElement>;
    /** Раскладка сетки: в недельном масштабе рамка выделяет недели целиком */
    grid: PlanGrid;
    enabled: boolean;
    onChange: (selection: CellSelection | null) => void;
}

/**
 * Выделение диапазона дней рамкой в пределах одной строки.
 *
 * По пустым дням рамка тянется левой кнопкой; чтобы выделить часть уже заполненного
 * блока (например, одну ячейку из отрезка), тянут средней кнопкой — левая по блоку
 * занята перетаскиванием. interact.js реагирует только на левую (mouseButtons: 1),
 * поэтому жесты не конфликтуют.
 */
export function useRangeSelection(options: RangeSelectionOptions) {
    const { containerRef, grid, enabled, onChange } = options;

    const handlers = useRef({ grid, onChange });
    handlers.current = { grid, onChange };

    useEffect(() => {
        const container = containerRef.current;

        if (!container || !enabled) {
            return;
        }

        let track: HTMLElement | null = null;
        let rowIndex = 0;
        let anchor = 0;

        const emit = (column: number) => {
            // над неделей без рабочих дней выделять нечего
            if (column < 0) {
                return;
            }

            const start = snapStart(handlers.current.grid, Math.min(anchor, column));
            const end = snapEnd(handlers.current.grid, Math.max(anchor, column));

            handlers.current.onChange({ rowIndex, start, length: end - start + 1 });
        };

        const onPointerMove = (event: PointerEvent) => {
            if (!track) {
                return;
            }

            event.preventDefault();
            emit(columnAt(track, event.clientX, handlers.current.grid));
        };

        const onPointerUp = () => {
            track = null;
            container.classList.remove(SELECTING_CLASS);
            document.removeEventListener("pointermove", onPointerMove, true);
            document.removeEventListener("pointerup", onPointerUp, true);
        };

        const onPointerDown = (event: PointerEvent) => {
            const middle = event.button === MIDDLE_BUTTON;

            if (event.button !== LEFT_BUTTON && !middle) {
                return;
            }

            const target = event.target as HTMLElement;
            const currentTrack = target.closest(`.${TRACK_CLASS}`) as HTMLElement | null;

            if (!currentTrack) {
                handlers.current.onChange(null);
                return;
            }

            const onBlock = !!target.closest(`.${BLOCK_CLASS}`) || !!target.closest(`.${SELECTION_CLASS}`);

            // левой кнопкой по заполненному блоку работает перетаскивание, а не рамка
            if (onBlock && !middle) {
                return;
            }

            if (middle) {
                // гасим автопрокрутку и вставку из первичного буфера
                event.preventDefault();
            }

            const start = columnAt(currentTrack, event.clientX, handlers.current.grid);

            // рамку нельзя начать с недели без рабочих дней
            if (start < 0) {
                return;
            }

            container.classList.add(SELECTING_CLASS);
            track = currentTrack;
            rowIndex = Number(currentTrack.getAttribute("data-row"));
            anchor = start;

            emit(anchor);

            document.addEventListener("pointermove", onPointerMove, true);
            document.addEventListener("pointerup", onPointerUp, true);
        };

        // средняя кнопка в браузере включает автопрокрутку — глушим её на сетке.
        // Когда рамка уже началась (pointerdown идёт раньше mousedown), гасим и левую:
        // иначе браузер заодно начинает выделять текст, и стоит увести курсор со строки —
        // подсвечиваются фамилии сотрудников
        const suppressAux = (event: MouseEvent) => {
            if (event.button === MIDDLE_BUTTON || track) {
                event.preventDefault();
            }
        };

        container.addEventListener("pointerdown", onPointerDown, true);
        container.addEventListener("mousedown", suppressAux, true);
        container.addEventListener("auxclick", suppressAux, true);

        return () => {
            container.removeEventListener("pointerdown", onPointerDown, true);
            container.removeEventListener("mousedown", suppressAux, true);
            container.removeEventListener("auxclick", suppressAux, true);
            container.classList.remove(SELECTING_CLASS);
            document.removeEventListener("pointermove", onPointerMove, true);
            document.removeEventListener("pointerup", onPointerUp, true);
        };
    }, [containerRef, enabled]);
}
